/**
 * TODO: migrate to develop branch
 * extension page register:
 * - <PlaceDetailPage />
 * ...more in `src\standard\route.js`
 */
import { useCallback, useMemo } from "react"
import { Routes, Route, useNavigate } from "react-router-dom"
import { AnimatePresence } from "framer-motion"
import { useIdleTimer } from "react-idle-timer"
import _ from "lodash"

import { useVenue } from "providers/venue"
import { useAppUI } from "./providers/ui"

// Theme
import { generateTheme } from "theme/generateTheme"
import { appThemeOptionFactory as directoryThemeOptionFactory } from "./theme/app/directory"

import { ThemeProvider } from "providers/mui-extended/ThemeProvider"

import { MapOrdinal } from "pages/maps/[ordinal].kiosk"
import { PlaceDetailPage } from "extensions/em-core/pages/maps/place/[placeId]/index.kiosk"
import { Layout as MapLayout } from "pages/maps/_layout.kiosk"
import { DirectionLayout } from "pages/maps/dir/[origin]/[destination]/_layout.kiosk"

import { DirectionOverviewPage } from "pages/maps/dir/[origin]/[destination]/index.kiosk"
import { StepByStep } from "pages/maps/dir/[origin]/[destination]/navigate/steps/[step]/index.kiosk"
import { HomePage } from "./pages/home/index.kiosk"
import { AmenityByCategory } from "./pages/maps/amenities/[category].kiosk"

import { AppLayout, PageLayout, MapPageLayout } from "layouts/kiosk-h"

import { ScrollToTop } from "components/ScrollToTop"
import { MotionDiv, showContent } from "./components/motions"
import { LoadingScreen, MapUpdatingScreen } from "./components/LoadingScreen"

import { homeNavigations, standardRoutes } from "standard"

import {
  getAllExtensionRoutes,
  getExtensionThemeFactories,
  mergeRoutes,
} from "./utils/extension"
import { extensionConfigs } from "./extensions"

import config from "./config"

const resetEvent = new Event("appreset")

// Extension routes
const device = config("device", "kiosk-h")
const extensionRoutes = getAllExtensionRoutes(
  config("venueExtension"),
  extensionConfigs,
  device
)

// Merge Standard + Extension routes
const mergedRoutes = mergeRoutes(_.get(standardRoutes, device), extensionRoutes)

// Extension Themes
const extensionThemeConfig = getExtensionThemeFactories(
  config("venueExtension"),
  extensionConfigs,
  device
)

// App Theme Factory
const appThemeOptionFactory = _.get(directoryThemeOptionFactory, device)

export const KioskHApp = () => {
  // Generate a venue theme
  const {
    themeOptions: venueThemeOptions,
    // refetchImdfData,
    dataLoaded,
  } = useVenue()
  const { mapReady, mapLoading } = useAppUI()
  const theme = generateTheme(venueThemeOptions, appThemeOptionFactory, {
    extendThemeFactories: extensionThemeConfig.themeFactories,
    ignoreAppThemeFactory: extensionThemeConfig.ignoreAppThemeFactory,
  })

  const navigate = useNavigate()
  const isAppReady = useMemo(
    () => mapReady && dataLoaded,
    [mapReady, dataLoaded]
  )
  const onIdle = useCallback(async () => {
    navigate("/")
    window.dispatchEvent(resetEvent)
    // TEMPORARY Disable : Refetch IMDF data on idle
    // await refetchImdfData()
  }, [navigate])

  useIdleTimer({
    onIdle,
    timeout: 1000 * 60 * 5,
    throttle: 500,
    disabled: false,
  })

  return (
    <ThemeProvider theme={theme}>
      <ScrollToTop />
      <AnimatePresence mode="sync">
        {!isAppReady && <LoadingScreen />}
        <MapUpdatingScreen key="map-updating" open={mapLoading} />
        <MotionDiv
          key="content"
          variants={showContent}
          initial="initial"
          animate="animate"
        >
          <Routes>
            <Route path="/" element={<AppLayout />}>
              <Route
                index
                element={<HomePage navigations={homeNavigations} />}
              />
              <Route path="/maps/*" element={<MapLayout />}>
                {/* The indoorPage parent router treats the route child parameter as the same parameter as its own. */}
                <Route
                  path="ordinal/:ordinal"
                  element={<MapOrdinal Layout={MapPageLayout} />}
                />
                {/* TODO: migrate to develop branch*/}
                <Route
                  path="place/:placeId"
                  element={<PlaceDetailPage Layout={MapPageLayout} />}
                />
                {/* <Route
                  path="amenities/:category"
                  element={<AmenityByCategory Layout={MapPageLayout} />}
                /> */}
                <Route
                  path="dir/:origin/:destination"
                  element={<DirectionLayout />}
                >
                  <Route
                    index
                    element={<DirectionOverviewPage Layout={MapPageLayout} />}
                  />
                  <Route
                    path="navigate/steps/:step"
                    element={<StepByStep Layout={MapPageLayout} />}
                  />
                </Route>
                <Route
                  path=":featureType/:category"
                  element={<AmenityByCategory Layout={MapPageLayout} />}
                />
              </Route>
              <Route path="/*" element={<PageLayout />}>
                {/* Add or override specific route extension pages here. */}
                {mergedRoutes.map(({ path, ...routeProps }) => (
                  <Route key={path} path={path} {...routeProps} />
                ))}
              </Route>
            </Route>
          </Routes>
        </MotionDiv>
      </AnimatePresence>
    </ThemeProvider>
  )
}
